import React, { useEffect, useRef, forwardRef, useState, useImperativeHandle } from 'react';
import { Stage, Layer, Rect, Path, Line, Group, Circle, Text, Ellipse } from 'react-konva';
import { degToRad } from '../../lib/ep';
import GameShooter from './GameShooter';

const default_player_svg_props = {
	stroke:'black',
	strokeWidth:1,
	lineCap:'round',
	lineJoin:'round',
};

const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:5, drawBorder:false };

const original_goal_svg_w = 425;

const PlayerHead = forwardRef(({ skin_color = '#C8A069', hair_color = '#645041', ...props }, ref) => {
	const refs = useRef([]);

	const genre = props?.genre ?? 'male';

	useImperativeHandle(ref, () => ({
		jump(direction) {
			if(refs.current['hair'] == undefined) return;

			refs.current['hair'].setAttrs({
				x:3 * direction,
			});

			if(genre != 'male') {
				refs.current['tail'].setAttrs({
					rotation:-50 * direction,
				});
			}
		},

		stand() {
			if(refs.current['hair'] == undefined) return;

			refs.current['hair'].setAttrs({
				x:0,
			});

			if(genre != 'male') {
				refs.current['tail'].setAttrs({
					rotation:0,
				});
			}
		},
	}), []);

	const has_hair = (genre == 'female' || hair_color != 'no') ? true : false;

	return (
		<>
			<Path
				{...default_player_svg_props}
				fill={skin_color}
				data="M0,2c-6.5,0-9.5,1.2-9.5,4C-9.5,8.4,0,6,0,6s9.5,2.4,9.5,0C9.5,3.2,6.5,2,0,2z"
				y={-1.5}
			/>

			<Path
				{...default_player_svg_props}
				fill={skin_color}
				data={(has_hair) ? "M0-6C0-6-7.5-6-7.5,6c0,5.19,2.9,8.5,7.5,8.5S7.5,11.19,7.5,6C7.5-6,0-6,0-6z" : "M0-6C-3.7-6-8.4-4-7.5,5c0.6,5.7,2.9,9.5,7.5,9.5S6.9,10.7,7.5,5C8.4-4,3.7-6,0-6z"}
				y={(has_hair) ? 0 : -1.5}
			/>

			<Path
				{...default_player_svg_props}
				fill={skin_color}
				data="M1.5,12l-0.92,2.12c-0.22,0.5-0.95,0.5-1.16,0L-1.5,12"
			/>

			{(has_hair) ? (
				<Path
					{...default_player_svg_props}
					ref={(node) => { refs.current['hair'] = node; }}
					fill={hair_color}
					data="M9-1c0-5.55-5.76-8-9-8c-3.24,0-9,2.19-9,8c0,3.37,0.73,6.85,1.5,9c1.14,3.19,4.14,4,7.5,4c3.78,0,5.03-2.75,5.5-6c0,0,1.22,2.16,2.25,1C8.53,6.12,9,1.94,9-1z"
				/>
			) : (
				<Path
					{...default_player_svg_props}
					data="M3.35,11.49c0,0-1.49,1-3.35,1s-3.35-1-3.35-1"
					y={-1.5}
				/>
			)}

			{(genre == 'female') && (
				<Group
					ref={(node) => { refs.current['tail'] = node; }}
					y={-8.5}
					rotation={0}
				>
					<Circle
						{...default_player_svg_props}
						fill='black'
						radius={3}
					/>

					<Path
						{...default_player_svg_props}
						fill={hair_color}
						data="M-2-9c0.98,3.19-1.5,4.89-1.5,7.5c0,1.94,1.79,4,3.5,4c2.41,0,3.5-1.74,3.5-4C3.5-3.58,1.99-6.08-2-9z"
					/>
				</Group>
			)}
		</>
	)
});

const PlayerShirt = forwardRef(({ shirt_color = '#9A4493', ...props }, ref) => {
	const [ jumping, setJumping ] = useState(false);

	const image = props?.image ?? false;

	const shirt_image = image?.fillPatternImage;
	const shirt_image_w = image?.image_w ?? 168;
	const shirt_image_h = image?.image_h ?? 84;

	useImperativeHandle(ref, () => ({
		jump(direction) {
			setJumping(direction);
		},

		stand() {
			setJumping(false);
		},
	}), []);

	let shirt_data;
	let fold_l_data;
	let fold_r_data;

	switch(jumping) {
		case -1:
			// left
			shirt_data = 'M4,22c-11.52-8.87-14.5-4.37-23-12c-0.92-0.82-1.44-2.2-4.5-2C-28.04,6.84-28-1-28-1s2.1-0.25,4.5-1c1.82-0.57,4.56-1.21,6.5-1.5C-9.15-4.69-4.78-6,0-6s8.6,0.51,14,1.5c4.36,0.8,7.15,1.51,9.5,2.5C26.2-0.87,28,0,28,0s-0.93,6.22-4.5,8c-3.79-0.51-3.79-0.51-4.5,3C17.86,15.46,15.16,22,4,22z';
			fold_l_data = 'M-19,10c-2.48-2.28-2-6-2-6';
			fold_r_data = 'M23.5,8C19.97,7.58,15,7,15,7';
			break;

		case 1:
			// right
			shirt_data = 'M-4,22c11.52-8.87,14.5-4.37,23-12c0.92-0.82,1.44-2.2,4.5-2C28.04,6.84,28-1,28-1s-2.1-0.25-4.5-1c-1.82-0.57-4.56-1.21-6.5-1.5C9.15-4.69,4.78-6,0-6s-8.6,0.51-14,1.5c-4.36,0.8-7.15,1.51-9.5,2.5C-26.2-0.87-28,0-28,0s0.93,6.22,4.5,8c3.79-0.51,3.79-0.51,4.5,3C-17.86,15.46-15.16,22-4,22z';
			fold_l_data = 'M19,10c2.48-2.28,2-6,2-6';
			fold_r_data = 'M-23.5,8c3.53-0.42,8.5-1,8.5-1';
			break;

		case 0:
		default:
			// rest
			shirt_data = 'M0,20.5c2.74,0,3.86,0.5,6.5,0.5c6,0,9.43-5.68,10.5-8c0,0,1,1.01,1.5,2c6.34,0,9.5-5,9.5-5L21.5,0c-1.03-1.61-2.56-3.21-4.5-3.5C9.15-4.69,4.78-6,0-6s-9.15,1.31-17,2.5c-1.94,0.29-3.47,1.89-4.5,3.5L-28,10c0,0,3.16,5,9.5,5c0.5-0.99,1.5-2,1.5-2c1.07,2.32,4.5,8,10.5,8C-3.86,21-2.74,20.5,0,20.5z';
			fold_l_data = 'M-17,13c-1.25-2.52-2-6-2-6';
			fold_r_data = 'M17,13c1.25-2.52,2-6,2-6';
			break;
	}

	return (
		<>
			<Path
				{...default_player_svg_props}
				strokeWidth={null}
				stroke={null}
				fill={shirt_color}
				fillPatternImage={shirt_image}
				fillPatternRepeat="no-repeat"
				fillPatternScaleX={56 / shirt_image_w}
				fillPatternScaleY={28 / shirt_image_h}
				fillPatternOffsetX={shirt_image_w / 2}
				fillPatternOffsetY={28 / 2}
				fillPriority={(image !== false) ? 'pattern' : 'fill'}
				data={shirt_data}
			/>

			<Path
				{...default_player_svg_props}
				fill='transparent'
				data={shirt_data}
			/>

			<Path
				{...default_player_svg_props}
				fill='transparent'
				data={fold_l_data}
			/>

			<Path
				{...default_player_svg_props}
				fill='transparent'
				data={fold_r_data}
			/>
		</>
	)
});

const PlayerBottomPart = forwardRef(({ pants_color = '#302A84', boot_color = '#DCDD3B', skin_color = '#C8A069', ...props }, ref) => {
	const [ jumping, setJumping ] = useState(false);

	useImperativeHandle(ref, () => ({
		jump(direction) {
			setJumping(direction);
		},

		stand() {
			setJumping(false);
		},
	}), []);

	let pants_data;
	let pants_line_data;
	let l_leg_data;
	let r_leg_data;
	let l_boot_data;
	let r_boot_data;
	let group_props = {};

	switch(jumping) {
		case -1:
		case 1:
			// left / right
			pants_data = 'M3,20c0,0,5.72,6.5,20,13c2.04,0,4.81-1.23,6-3c0,0,3.46-0.67,5-2c5.63-4.87,7-9,7-9C24.03,13.98,29,7,18,7L3,20z';
			pants_line_data = 'M29,30c-8.17-1.49-9.9-5.02-16-6';
			l_leg_data = 'M43,29c-5.89,1.98-13.22,4.09-20,3.5V22c14.16,1.01,11.3,0,20,0V29z';
			r_leg_data = 'M29,19c16.69,4.98,10.53-1.31,29,5c0,0,4.58,3.88,0,5c-6.79,0-18.86,3.2-29,0V19z';
			l_boot_data = 'M51,22c0,0,1.95-2.22,3.5-2.5c4.27,1.03,5.89,3.57,10.5,6c2.08,1.1,4,2.81,4,4c0,1.24-0.91,1.5-2,1.5c-4.93,0-9.89-3.2-16-4V22z';
			r_boot_data = 'M58,24c0,0,2.37-2,4-2c4.17,1.78,5.73,4.78,10,8c1.93,1.46,3,3.31,3,4.5c0,1.07-0.87,1.5-2,1.5c-5.17,0-8.92-5.12-15-7V24z';

			if(jumping > 0) group_props.scaleX = -1;
			break;

		case 0:
		default:
			// rest
			pants_data = 'M14.5,10c0,0,0.49,4.25,3,16c0,0-0.03,3.4-6,4c-3.87,0.39-4.74-0.29-6-2C2.87,24.45,0,23,0,23s-2.87,1.45-5.5,5c-1.26,1.71-2.13,2.39-6,2c-5.97-0.6-6-4-6-4c2.51-11.75,3-16,3-16H14.5z';
			pants_line_data = false;
			l_leg_data = false;
			r_leg_data = false;
			l_boot_data = 'M-6,28c-2.1,6.08-4.97,10-7.5,10c-1.76,0-2.75-1.43-2.75-4c0-2.1,0.58-5.62,1.75-9L-6,28z';
			r_boot_data = 'M6,28c2.1,6.08,4.97,10,7.5,10c1.76,0,2.75-1.43,2.75-4c0-2.1-0.58-5.62-1.75-9L6,28z';
			break;
	}

	return (
		<Group
			{...group_props}
		>
			<Ellipse
				fill='black'
				opacity={0.2}
				x={(jumping != 0) ? 15 : 0}
				y={(jumping != 0) ? 40 : 35}
				radius={{
					x:(jumping != 0) ? 40 : 25,
					y:12.5,
				}}
			/>

			<Path
				{...default_player_svg_props}
				fill={boot_color}
				data={l_boot_data}
			/>

			<Path
				{...default_player_svg_props}
				fill={boot_color}
				data={r_boot_data}
			/>

			{(l_leg_data !== false) && (
				<Path
					{...default_player_svg_props}
					fill={skin_color}
					data={l_leg_data}
				/>
			)}

			{(r_leg_data !== false) && (
				<Path
					{...default_player_svg_props}
					fill={skin_color}
					data={r_leg_data}
				/>
			)}

			<Path
				{...default_player_svg_props}
				fill={pants_color}
				data={pants_data}
			/>

			{(pants_line_data !== false) && (
				<Path
					{...default_player_svg_props}
					fill='transparent'
					data={pants_line_data}
				/>
			)}
		</Group>
	)
});

const GoalKeeperArm = ({ skin_color = '#C8A069', glove_color = '#FFFFFF', ...props }) => {
	return (
		<>
			<Path
				{...default_player_svg_props}
				fill={skin_color}
				data="M-13,25.5c1.85-3.16,6.77-9.99,11.5-16.5C0.7,5.97,2.36,4.88,4,2.4C5.24,0.53,4.46-1.89,2.5-3S-1.75-3.35-3-1.5c-6.32,9.34-10.34,17.92-13.5,25L-13,25.5z"
			/>

			<Path
				{...default_player_svg_props}
				fill={glove_color}
				data="M-11.25,35c0.82-1.74,0.45-3.39-0.25-5c-0.8-1.85-0.6-3.57,0-5l-5.5-3c0,0-1.35,3.14-2.5,5c-3.14,5.09-2.62,8.16,1.5,10C-15.21,38.24-12.88,38.47-11.25,35z"
			/>
		</>
	)
};

const BarrierArm = ({ skin_color = '#C8A069', ...props }) => {
	return (
		<Path
			{...default_player_svg_props}
			fill={skin_color}
			data="M-28,12c0,0.8,0.23,1.69,1,2.5c7.23,7.6,14.25,10.31,19.5,14.5c1.88,1.5,3.29,1.5,4,1.5c1.42,0,3.5-1.2,3.5-3.5c0-1.1-0.55-1.6-1.5-2c-2.08-0.87-4.37,0.42-6.5-1c-2.06-1.36-7.07-4.85-10.5-9c-1.31-1.58-2.07-4.61-3.5-6c-0.77-0.75-1.76-1-3-1C-25.38,8-26.82,8.98-28,12z"
		/>
	)
};

const FootballGoalKeaper = forwardRef((props, ref) => {
	const player_w = 75;
	const player_h = 49;

	const used_w = props?.width ?? player_w;

	const scale = used_w / player_w;

	const strokeWidth = default_player_svg_props.strokeWidth / scale;

	const passed_props = { ...props, strokeWidth };

	const refs = useRef([]);
	const waiting_direction = useRef(1);
	const moving_direction = useRef(1);
	const is_waiting = useRef(true);

	const animateWandering = (max_spread) => {
		if(is_waiting.current === false) return;
		if(refs.current['player'] == undefined) return;

		const spread_multiplier = 0.1 + 0.9 * Math.random();

		refs.current['player'].to({
			x:spread_multiplier * max_spread * moving_direction.current + used_w / 2,
			duration:spread_multiplier * 1.75,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				if(is_waiting.current === false) return;
				if(refs.current['player'] == undefined) return;

				moving_direction.current = -1 * moving_direction.current;
				animateWandering(max_spread);
			},
		});
	};

	const resetWandering = () => {
		if(refs.current['player'] == undefined) return;

		refs.current['player'].to({
			x:used_w / 2,
			duration:1.75,
			easing:Konva.Easings.StrongEaseInOut,
		});
	};

	const animateWholePlayer = () => {
		if(is_waiting.current === false) return;
		if(refs.current['player'] == undefined) return;

		refs.current['player'].to({
			x:2 * moving_direction.current + used_w / 2,
			//x:used_w / 4 * moving_direction.current + used_w / 2,
			duration:1.3 * Math.random() + 0.2,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				if(is_waiting.current === false) return;
				if(refs.current['player'] == undefined) return;

				moving_direction.current = -1 * moving_direction.current;
				animateWholePlayer();
			},
		});
	};

	const animateUpperPartWaiting = () => {
		if(is_waiting.current === false) return;
		if(refs.current['upper_part'] == undefined) return;

		refs.current['left_arm'].to({
			rotation:-7 * waiting_direction.current,
			duration:1,
			easing:Konva.Easings.BounceEaseOut,
		});

		refs.current['right_arm'].to({
			rotation:7 * waiting_direction.current,
			duration:1,
			easing:Konva.Easings.BounceEaseOut,
		});

		refs.current['upper_part'].to({
			x:3 * waiting_direction.current,
			rotation:3 * waiting_direction.current,
			duration:0.8 * Math.random() + 0.3,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				if(is_waiting.current === false) return;
				if(refs.current['upper_part'] == undefined) return;

				setTimeout(() => {
					waiting_direction.current = -1 * waiting_direction.current;
					animateUpperPartWaiting();
				}, 300);
			},
		})
	};

	const applyCache = () => {
		if(refs.current['player'] != undefined) {
			refs.current['player'].cache(default_cache_props);
		}
	};

	const applyCacheByParts = () => {
		if(refs.current['left_arm'] != undefined) {
			refs.current['left_arm'].cache(default_cache_props);
			refs.current['right_arm'].cache(default_cache_props);
			refs.current['head_c'].cache(default_cache_props);
			refs.current['shirt_c'].cache(default_cache_props);
			refs.current['bottom_part_c'].cache(default_cache_props);
		}
	};

	useImperativeHandle(ref, () => ({
		startWaiting() {
			applyCacheByParts();
			is_waiting.current = true;
			waiting_direction.current = 1;
			animateUpperPartWaiting();
			animateWholePlayer();
		},

		stopWaiting() {
			is_waiting.current = false;
		},

		wander(max_spread) {
			applyCache();
			is_waiting.current = true;
			waiting_direction.current = 1;
			animateWandering(max_spread);
		},

		stopWander() {
			is_waiting.current = false;
			resetWandering();
		},

		jump(direction) {
			refs.current['head_c'].setAttrs({
				x:10 * direction,
				y:-4,
			});

			refs.current['left_arm'].to({
				rotation:(direction > 0) ? 50 : 40,
				duration:0.7,
				easing:Konva.Easings.BounceEaseOut,
			});

			refs.current['right_arm'].to({
				rotation:(direction > 0) ? -40 : -50,
				duration:0.7,
				easing:Konva.Easings.BounceEaseOut,
			});

			refs.current['head'].jump(direction);
			refs.current['shirt'].jump(direction);
			refs.current['bottom_part'].jump(direction);

			applyCacheByParts();
		},

		stand() {
			refs.current['head_c'].setAttrs({
				x:0,
				y:0,
			});

			refs.current['left_arm'].setAttrs({
				rotation:0,
			});

			refs.current['right_arm'].setAttrs({
				rotation:0,
			});

			refs.current['head'].stand();
			refs.current['shirt'].stand();
			refs.current['bottom_part'].stand();

			applyCacheByParts();
		},
	}), []);

	return (
		<Group
			ref={(node) => { refs.current['player'] = node; }}
			x={used_w / 2}
			y={10 * scale}	// 10 is the distance from (0, 0) to the top of the head
			scaleX={scale}
			scaleY={scale}
		>
			<Group
				ref={(node) => { refs.current['bottom_part_c'] = node; }}
			>
				<PlayerBottomPart
					ref={(node) => { refs.current['bottom_part'] = node; }}
					{...passed_props}
				/>
			</Group>

			<Group
				ref={(node) => { refs.current['upper_part'] = node; }}
			>
				<Group
					ref={(node) => { refs.current['left_arm'] = node; }}
					x={-16}
					y={1.1667}
				>
					<GoalKeeperArm {...passed_props} />
				</Group>

				<Group
					ref={(node) => { refs.current['right_arm'] = node; }}
					x={16}
					y={1.1667}
					scaleX={-1}
				>
					<GoalKeeperArm {...passed_props} />
				</Group>

				<Group
					ref={(node) => { refs.current['shirt_c'] = node; }}
				>
					<PlayerShirt
						ref={(node) => { refs.current['shirt'] = node; }}
						{...passed_props}
					/>
				</Group>

				<Group
					ref={(node) => { refs.current['head_c'] = node; }}
				>
					<PlayerHead
						ref={(node) => { refs.current['head'] = node; }}
						{...passed_props}
					/>
				</Group>
			</Group>
		</Group>
	)
});

const FootballBarrier = forwardRef((props, ref) => {
	const player_w = 56;
	const player_h = 48;

	const used_w = props?.width ?? player_w;

	const scale = used_w / player_w;

	const index = props?.index ?? 0;

	const refs = useRef([]);
	const moving_direction = useRef(1);
	const is_waiting = useRef(false);
	const animation_index = useRef(0);
	const already_moved = useRef(false);

	const applyCache = () => {
		if(refs.current[`player_${index}`] != undefined) {
			refs.current[`player_${index}`].cache(default_cache_props);
		}
	};

	const animateWandering = (max_spread, level, count) => {
		if(is_waiting.current === false) return;
		if(refs.current[`player_${index}`] == undefined) return;

		let level_speed;
		switch(level) {
			case 'easy':
				level_speed = 2.5;
				break;

			case 'hard':
				level_speed = 1.5;
				break;

			case 'normal':
			default:
				level_speed = 2;
				break;
		}

		const speed = refs.current[`player_${index}`].attrs.speed;

		setTimeout(() => {
			if(is_waiting.current === false) return;
			if(refs.current[`player_${index}`] == undefined) return;

			refs.current[`player_${index}`].to({
				x:max_spread * moving_direction.current + used_w / 2,
				duration:speed + level_speed,
				easing:Konva.Easings.EaseInOut,
				onFinish:() => {
					if(is_waiting.current === false) return;
					if(refs.current[`player_${index}`] == undefined) return;
					if(animation_index.current != count) return;

					moving_direction.current = -1 * moving_direction.current;
					animateWandering(max_spread, level, count);
				},
			});
		}, 200);
	};

	const resetWandering = () => {
		if(refs.current[`player_${index}`] == undefined) return;

		refs.current[`player_${index}`].to({
			x:used_w / 2,
			duration:1,
			easing:Konva.Easings.StrongEaseInOut,
		});
	};

	useImperativeHandle(ref, () => ({
		wander(max_spread, level, speed = 1, direction = 1) {
			if(is_waiting.current) return;

			if(already_moved.current === false) {
				applyCache();
				already_moved.current = true;
			}
			
			is_waiting.current = true;

			moving_direction.current = direction;

			refs.current[`player_${index}`].setAttrs({
				speed,
			});

			animateWandering(max_spread, level, animation_index.current);
		},

		stopWander() {
			animation_index.current++;
			is_waiting.current = false;
			resetWandering();
		},
	}), []);

	const strokeWidth = default_player_svg_props.strokeWidth / scale;

	const passed_props = { ...props, strokeWidth };

	return (
		<Group
			ref={(node) => { refs.current[`player_${index}`] = node; }}
			x={used_w / 2}
			y={10 * scale}	// 10 is the distance from (0, 0) to the top of the head
			scaleX={scale}
			scaleY={scale}
			speed={1}
		>
			<PlayerBottomPart
				{...passed_props}
			/>

			<BarrierArm
				{...passed_props}
			/>

			<Group
				scaleX={-1}
			>
				<BarrierArm
					{...passed_props}
				/>
			</Group>

			<PlayerShirt
				{...passed_props}
				image={props?.shirt_image ?? false}
			/>

			<PlayerHead
				{...passed_props}
			/>
		</Group>
	)
});

const FoosballBar = ({ w, ...props }) => {
	return (
		<>
			<Rect
				width={w}
				height={10 * 0.7}
				fill='black'
				opacity={0.2}
				y={10}
			/>

			<Rect
				width={w}
				height={10}
				fillLinearGradientStartPoint={{ x:0, y:0 }}
				fillLinearGradientEndPoint={{ x:0, y:10 }}
				fillLinearGradientColorStops={[0, 'rgba(200, 200, 200, 1)', 0.3, 'rgba(255, 255, 255, 1)', 0.7, 'rgba(100, 100, 100, 1)', 1, 'rgba(50, 50, 50, 1)']}
			/>
		</>
	)
}

const FootballBall = ({ ball_props, ...props }) => {
	const default_props = {
		strokeWidth:0.5 / ball_props.scaleX,
		stroke:'black',
	};

	const line_props = {
		...default_props,
		fill:'transparent'
	};

	const solids_props = {
		...default_props,
		fill:'black',
	};

	const circle_props = {
		strokeWidth:1 / ball_props.scaleX,
		stroke:'black',
		fill:'white',
		radius:30,
	};

	return (
		<Group {...ball_props}>
			<Circle {...circle_props} />

			<Line
				{...line_props}
				points={[-6.41, 9.87, -11.98, 16.49]}
			/>

			<Line
				{...line_props}
				points={[10.37, -2.33, 19.38, -6.3]}
			/>

			<Line
				{...line_props}
				points={[0, -9.87, 0, -20.38]}
			/>

			<Line
				{...line_props}
				points={[-10.37, -2.33, -19.38, -6.3]}
			/>

			<Line
				{...line_props}
				points={[6.41, 9.87, 11.98, 16.49]}
			/>

			<Path
				{...line_props}
				data="M5.71,25.75C3.87,26.16,1.96,26.24,0,26.24c-1.96,0-3.88-0.09-5.72-0.49"
			/>

			<Path
				{...line_props}
				data="M26.26,2.53c-0.18,1.87-0.69,3.72-1.3,5.58c-0.61,1.87-1.28,3.66-2.24,5.29"
			/>

			<Path
				{...line_props}
				data="M10.52-24.19c1.73,0.75,3.32,1.81,4.91,2.96c1.59,1.15,3.09,2.35,4.34,3.76"
			/>

			<Path
				{...line_props}
				data="M-19.76-17.48c1.25-1.41,2.74-2.6,4.33-3.75c1.59-1.15,3.19-2.21,4.92-2.96"
			/>

			<Path
				{...line_props}
				data="M-22.73,13.39c-0.95-1.62-1.63-3.41-2.23-5.28c-0.61-1.87-1.12-3.72-1.3-5.59"
			/>

			<Path
				{...solids_props}
				data="M-10.98,27.9c1.87-0.39,5.1-1.66,5.25-2.15c0.22-0.68-5.8-8.93-6.26-9.26c-0.47-0.34-10.13-3.51-10.74-3.09c-0.36,0.25-0.63,3.79-0.4,5.71C-19.92,22.98-15.74,26.02-10.98,27.9z"
			/>

			<Path
				{...solids_props}
				data="M22.71,13.41c-0.58-0.42-10.28,2.76-10.74,3.09c-0.47,0.34-6.47,8.55-6.26,9.26c0.12,0.42,3.37,1.75,5.27,2.14c4.76-1.88,8.94-4.91,12.15-8.8C23.35,17.21,23.13,13.71,22.71,13.41z"
			/>

			<Path
				{...solids_props}
				data="M19.77-17.46c-0.58,0.42-0.55,10.63-0.38,11.17c0.18,0.55,6.13,8.8,6.87,8.82c0.43,0.01,2.69-2.64,3.65-4.33c-0.31-5.25-1.96-10.13-4.63-14.31C23.54-16.89,20.18-17.76,19.77-17.46z"
			/>

			<Path
				{...solids_props}
				data="M-7.53-29.01c-1.28,1.42-3.14,4.33-2.98,4.81c0.22,0.68,9.94,3.81,10.51,3.81c0.58,0,10.26-3.11,10.51-3.81c0.15-0.41-1.69-3.39-3-4.82C5.11-29.64,2.6-30,0-30C-2.61-30-5.12-29.63-7.53-29.01z"
			/>

			<Path
				{...solids_props}
				data="M-19.76-17.47c-0.35-0.27-3.76,0.56-5.52,1.37c-2.67,4.18-4.32,9.06-4.63,14.31c0.95,1.66,3.14,4.31,3.65,4.31c0.71,0,6.7-8.28,6.87-8.81C-19.2-6.85-19.17-17.02-19.76-17.47z"
			/>

			<Path
				{...solids_props}
				data="M0-9.87c0-1.04-11.46,7.29-10.37,7.54C-11.46-2.57-7.09,10.9-6.41,9.87c-0.67,1.04,13.5,1.04,12.82,0c0.67,1.04,5.05-12.44,3.96-12.19C11.46-2.57,0-10.9,0-9.87z"
			/>
		</Group>
	)
};

const GoalTargetVisual = (props) => {
	const color = props?.color ?? 'rgba(255, 255, 255, 1)';

	const original_w = props?.original_w ?? original_goal_svg_w;
	const final_w = props?.final_w ?? original_w;

	const structure_props = {
		strokeWidth:2.5,
		stroke:color,
		lineCap:'round',
		lineJoin:'round',
	};

	const net_props = {
		...structure_props,
		strokeWidth:1.5,
		opacity:0.7,
	};

	const final_scale = final_w / original_w;

	return (
		<Group
			x={final_w / 2}
			offsetX={original_w / 2}
			scaleX={final_scale}
		>
			<Line
				{...structure_props}
				points={[-212.5, 85, -198.33, 5, -198.33, 53, 198.41, 53, 198.41, 5, 212.5, 85]}
			/>

			<Line
				{...structure_props}
				points={[-198.41, 53, -212.5, 165]}
			/>

			<Line
				{...structure_props}
				points={[198.41, 53, 212.5, 165]}
			/>

			<Group
				scaleY={1 / Math.max(1, final_scale)}
			>
				<Path
					{...net_props}
					data="M198.41,5C198.41,5,97.36,24.22,0,9.36C-97.36,24.22-198.33,5-198.33,5"
				/>

				<Path
					{...net_props}
					data="M200.18,15c0,0-114.98,16.82-200.18,3.82C-85.19,31.82-200.1,15-200.1,15"
				/>

				<Path
					{...net_props}
					data="M201.94,25c0,0-128.91,14.42-201.94,3.27C-73.02,39.42-201.87,25-201.87,25"
				/>

				<Path
					{...net_props}
					data="M203.7,35c0,0-142.84,12.02-203.7,2.73C-60.85,47.02-203.64,35-203.64,35"
				/>

				<Path
					{...net_props}
					data="M205.46,45c0,0-156.78,9.61-205.46,2.18C-48.68,54.61-205.42,45-205.42,45"
				/>

				<Path
					{...net_props}
					data="M207.22,55c0,0-170.71,7.21-207.22,1.64C-36.51,62.21-207.19,55-207.19,55"
				/>

				<Path
					{...net_props}
					data="M208.98,65c0,0-184.64,4.81-208.98,1.09C-24.34,69.81-208.96,65-208.96,65"
				/>

				<Path
					{...net_props}
					data="M210.74,75c0,0-198.57,2.4-210.74,0.55C-12.17,77.4-210.73,75-210.73,75"
				/>

				<Line
					{...net_props}
					points={[-190.65, 85, -173.77, 8.23]}
				/>
				
				<Line
					{...net_props}
					points={[-168.22, 85, -153.81, 10.77]}
				/>
				
				<Line
					{...net_props}
					points={[-145.79, 85, -133.63, 12.72]}
				/>
				
				<Line
					{...net_props}
					points={[-123.36, 85, -113.27, 14.09]}
				/>
				
				<Line
					{...net_props}
					points={[-100.93, 85, -92.76, 14.85]}
				/>
				
				<Line
					{...net_props}
					points={[-78.5, 85, -72.16, 14.96]}
				/>
				
				<Line
					{...net_props}
					points={[-56.07, 85, -51.5, 14.37]}
				/>
				
				<Line
					{...net_props}
					points={[-33.64, 85, -31.33, 13.05]}
				/>
				
				<Line
					{...net_props}
					points={[-11.21, 85, -10.25, 10.81]}
				/>
				
				<Line
					{...net_props}
					points={[11.21, 85, 10.25, 10.81]}
				/>
				
				<Line
					{...net_props}
					points={[33.64, 85, 30.85, 13.01]}
				/>
				
				<Line
					{...net_props}
					points={[56.07, 85, 51.5, 14.37]}
				/>
				
				<Line
					{...net_props}
					points={[78.5, 85, 72.16, 14.96]}
				/>
				
				<Line
					{...net_props}
					points={[100.93, 85, 92.76, 14.85]}
				/>
				
				<Line
					{...net_props}
					points={[123.36, 85, 113.27, 14.09]}
				/>
				
				<Line
					{...net_props}
					points={[145.79, 85, 133.63, 12.72]}
				/>
				
				<Line
					{...net_props}
					points={[168.22, 85, 153.81, 10.77]}
				/>
				
				<Line
					{...net_props}
					points={[190.65, 85, 173.77, 8.23]}
				/>
			</Group>

			<Line
				{...structure_props}
				scaleY={Math.max(1, final_scale)}
				offsetY={160}
				y={160}
				strokeWidth={7}
				points={[-212.5, 165, -212.5, 85, 212.5, 85, 212.5, 165]}
			/>
		</Group>
	)
};

const FieldLines = (props) => {
	const color = props?.color ?? 'rgba(255, 255, 255, 1)';

	const line_props = {
		strokeWidth:4,
		stroke:color,
	};

	return (
		<>
			<Line
				{...line_props}
				points={[ 0, 165, 1200, 165 ]}
			/>

			<Line
				{...line_props}
				points={[ 0, 855, 1200, 855 ]}
			/>

			<Line
				{...line_props}
				x={200}
				points={[ 740, 165, 740, 395, 60, 395, 60, 165 ]}
			/>

			<Path
				{...line_props}
				x={200}
				data="M727.5,855c-72.43,102.82-192.12,170-327.5,170S144.93,957.82,72.5,855"
			/>

			<Circle
				x={600}
				y={625}
				radius={7.5}
				fill={color}
			/>
		</>
	)
};

const FoosballFieldLines = (props) => {
	const original_w = props?.original_w ?? original_goal_svg_w;
	const final_w = props?.final_w ?? original_w;

	const color = props?.color ?? 'rgba(255, 255, 255, 1)';

	const line_props = {
		strokeWidth:4,
		stroke:color,
	};

	return (
		<>
			<Line
				{...line_props}
				x={600}
				y={165}
				scaleX={Math.max(1, 0.9 * final_w / original_w)}
				points={[350, 0, 350, 300, -350, 300, -350, 0]}
			/>

			<Path
				{...line_props}
				x={200}
				data="M575,465c-38.7,48.39-102.66,80-175,80s-136.3-31.61-175-80"
			/>

			<Line
				{...line_props}
				x={600}
				y={165}
				scaleX={Math.max(1, final_w / original_w)}
				strokeWidth={2}
				points={[-original_goal_svg_w / 2, 0, original_goal_svg_w / 2, 0]}
				opacity={0.5}
				//dash={[12, 12]}
			/>

			<Circle
				{...line_props}
				x={600}
				y={990}
				radius={250}
			/>

			<Circle
				x={600}
				y={990}
				radius={12.5}
				fill={color}
			/>

			<Line
				{...line_props}
				points={[0, 990, 1200, 990]}
			/>
		</>
	)
};

const FeedbackMessage = forwardRef(({ total_goals, ...props }, ref) => {
	const visible_y = props?.visible_y ?? 0;
	const invisible_y = visible_y + 250;

	const refs = useRef([]);
	const goals = useRef(parseInt(props?.done_goals ?? 0));

	const showMessage = (msg = "", cb) => {
		if(msg == "") return;
		if(refs.current['message'] == undefined) return;
		if(refs.current['message_c'] == undefined) return;
		
		refs.current['message'].setAttrs({
			text:msg,
		});
	
		refs.current['message_c'].cache(default_cache_props);
		
		refs.current['message_c'].to({
			y:0,
			duration:0.5,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				setTimeout(() => {
					if(refs.current['message_c'] == undefined) return;

					refs.current['message_c'].to({
						y:150,
						duration:0.5,
						easing:Konva.Easings.StrongEaseInOut,
						onFinish:() => {
							if(cb != undefined) cb();
						},
					});
				}, 1500);
			},
		});
	};

	const hide = (cb) => {
		if(refs.current['whole'] == undefined) return;

		refs.current['whole'].to({
			y:invisible_y,
			duration:1,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				if(cb != undefined) cb();
			},
		});
	};

	const addNewGoal = (add_new_goal = false, cb) => {
		if(add_new_goal === false) {
			if(cb != undefined) {
				setTimeout(() => {
					cb();
				}, 400);
			}
			return;
		}

		goals.current++;

		setTimeout(() => {
			refs.current[`done_goal_${goals.current - 1}`].to({
				rotation:360,
				scaleX:1,
				scaleY:1,
				duration:1,
				easing:Konva.Easings.StrongEaseInOut,
			});
		}, 300);

		if(cb != undefined) {
			setTimeout(() => {
				cb();
			}, 150);
		}
	};

	useImperativeHandle(ref, () => ({
		display(msg = "", add_new_goal = false, cb) {
			refs.current['whole'].to({
				y:visible_y,
				duration:1,
				easing:Konva.Easings.StrongEaseInOut,
			});

			addNewGoal(add_new_goal, () => {
				setTimeout(() => {
					if(msg != "") {
						showMessage(msg, () => {
							hide(cb);
						});
					} else {
						hide(cb);
					}
				}, ((msg != "") ? 300 : 700));
			});
		},
	}), []);

	useEffect(() => {
		[...Array(total_goals).keys()].map((goal, g) => {
			refs.current[`done_goal_${g}`].cache(default_cache_props);
		});
	}, []);

	const line_props = {
		stroke:'white',
		strokeWidth:4,
	};

	return (
		<Group
			ref={(node) => { refs.current['whole'] = node; }}
			{...props?.msg_props ?? {}}
			y={invisible_y}
		>
			<Group
				clipFunc={(ctx) => {
					ctx.rect(-20, -20, 1240, 170);
				}}
			>
				<Group
					ref={(node) => { refs.current['message_c'] = node; }}
					y={150}
				>
					<Rect
						{...line_props}
						x={-20}
						width={1240}
						height={150}
						fill='rgba(0, 0, 0, 0.8)'
					/>

					<Text
						ref={(node) => { refs.current['message'] = node; }}
						width={750}
						height={100}
						x={225}
						y={42}
						text="GOOOOOAL!"
						fill='white'
						fontSize={60}
						fontStyle='bold'
						fontFamily="Arial"
						align='center'
						verticalAlign='center'
					/>
				</Group>
			</Group>

			<Rect
				x={-20}
				y={130}
				width={1240}
				height={100}
				fill='black'
			/>

			<Line
				{...line_props}
				points={[-20, 0, 1220, 0]}
				y={230}
			/>

			<Line
				{...line_props}
				points={[-20, 0, 1220, 0]}
				y={130}
				strokeWidth={2}
			/>

			<Group
				x={(1200 - (total_goals - 1) * 70) / 2}
			>
				{[...Array(total_goals).keys()].map((goal, g) => {
					return (
						<Circle
							key={`goal_${g}`}
							x={g * 70}
							y={180}
							radius={15}
							fill='white'
							opacity={0.4}
						/>
					)
				})}

				{[...Array(total_goals).keys()].map((goal, g) => {
					return (
						<Group
							key={`done_goal_${g}`}
							ref={(node) => { refs.current[`done_goal_${g}`] = node; }}
							x={g * 70}
							y={180}
							rotation={0}
							scaleX={0}
							scaleY={0}
						>
							<Circle
								radius={28}
								fill='white'
							/>

							<FootballBall
								ball_props={{
									scaleX:25 / 30,
									scaleY:25 / 30,
								}}
							/>
						</Group>
					)
				})}
			</Group>
		</Group>
	)
});

const PenaltyCanvas = forwardRef(({ stage, ...props }, ref) => {
	const level = stage?.penalty_level ?? 'normal';
	const mode = stage?.penalty_mode ?? 'real';
	const display_outter_border = stage?.display_outter_border ?? true;
	const barrier_length = parseInt(stage?.penalty_barrier_length ?? '0');
	const bg_color = stage?.bg_color ?? ((mode != 'foosball') ? 'rgba(90, 150, 50, 1)' : 'rgba(60, 95, 45, 1)');
	const relocate_ball = stage?.penalty_relocate_ball ?? false;
	const display_field_texture = stage?.penalty_display_field_texture ?? true;
	const num_goals = parseInt(stage?.penalty_num_goals ?? '1');
	const gk_skin_color = stage?.penalty_goal_keeper_skin_color ?? 'random';
	const gk_hair_color = stage?.penalty_goal_keeper_hair_color ?? 'random';
	const genre = stage?.penalty_team_genre ?? 'male';
	const b_skin_color = stage?.penalty_barrier_skin_color ?? 'random';
	const b_hair_color = stage?.penalty_barrier_hair_color ?? 'random';
	const cover_color = stage?.penalty_foosball_cover_color ?? 'rgba(160, 85, 20, 1)';
	const field_lines_color = stage?.penalty_field_lines_color ?? 'rgba(255, 255, 255, 1)';
	const reaction_messages = stage?.penalty_reaction_messages ?? [];
	const shooter_color = stage?.shooter_color ?? 'rgba(255, 255, 255, 1)';

	let move_barrier = ((stage?.penalty_move_barrier ?? 'barrier') == 'free') ? true : false;
	if(mode == 'foosball') move_barrier = false;

	const [ full_loaded, setFullLoaded ] = useState(false);

	const h_prop = ((window?.platform ?? 'desktop') == 'desktop') ? (10 / 8) : (12 / 8);
	const default_size_w = 600;
	const default_size_h = default_size_w * h_prop

	const is_builder = props?.is_builder ?? false;
	const is_test = props?.is_test ?? false;
	
	const sorted_barrier_players = [...Array(barrier_length).keys()].sort((a, b) => {
		if(a % 2 == 0) return 1;
		return -1;
	});

	const points_for_goal = 100;
	const points_for_fail = -1 * Math.abs(parseInt(stage?.penalty_points_for_fail ?? '0'));

	const delay_to_success = 5; // in seconds
	const is_debug = false;

	const skin_colors = ['#E9C085', '#C8A069', '#997650', '#56422E', '#35281C', 'no'];
	const hair_colors = ['#C1BBB6', '#C4BE74', '#9B7E37', '#645041', '#3B2E28', '#15110F'];
	if(genre == 'male') hair_colors.push('no');

	const getRandomColor = (type = 'skin') => {
		const arr = (type == 'skin') ? [ ...skin_colors ] : [ ...hair_colors ];
		return arr[Math.floor(Math.random() * arr.length)];
	};

	const refs = useRef([]);
	const size = useRef({ w:default_size_w, h:default_size_h });
	const shot_duration = useRef(0);
	const animations = useRef(false);
	const game_ended = useRef(false);
	const detector = useRef(false);
	const jump_needed = useRef(false);
	const game_score = useRef(0);
	const actual_goals = useRef(0);
	const barrier_init_x = useRef([]);
	const is_playable = useRef((!is_builder || is_test) ? true : false);
	const goal_keeper_skin_color = useRef(getRandomColor('skin'));
	const goal_keeper_hair_color = useRef(getRandomColor('hair'));
	const barrier_skin_colors = useRef([]);
	const barrier_hair_colors = useRef([]);

	const initial_ball_coords = {
		x:size.current.w / 2,
		y:1.65 * size.current.h / 2,
	};

	const ball_coords = useRef(initial_ball_coords);

	const scenario_scale = size.current.h / 1200;
	const scenario_scale_w = size.current.w / 800;

	const goal_target_w_original = original_goal_svg_w;
	const goal_target_y = 165 * scenario_scale;
	const goal_target_w = goal_target_w_original * scenario_scale_w;
	const goal_target_h = 80 * scenario_scale;

	const logo_size = 150 * scenario_scale / scenario_scale_w;
	const ball_o_size = 60 * scenario_scale / scenario_scale_w;

	const ball_image = props?.indexed_images?.penalty_ball_image ?? false;
	let ball_size = 1;
	if(ball_image !== false) {
		ball_size = ball_o_size / Math.min((ball_image?.image_w ?? 120), (ball_image?.image_h ?? 120));
	}

	const right_logo = props?.indexed_images?.penalty_right_logo ?? false;
	let right_logo_size = 1;
	if(right_logo !== false) {
		right_logo_size = logo_size / Math.min((right_logo?.image_w ?? 300), (right_logo?.image_h ?? 300));
	}

	const left_logo = props?.indexed_images?.penalty_left_logo ?? false;
	let left_logo_size = 1;
	if(left_logo !== false) {
		left_logo_size = logo_size / Math.min((left_logo?.image_w ?? 300), (left_logo?.image_h ?? 300));
	}

	const reset_props = {
		duration:0.5,
		easing:Konva.Easings.BounceEaseOut,
	};

	/* SOUNDS - ini */
	const kickPlay = () => props.sound_fx.kick.play();
	const goalPlay = () => props.sound_fx.goal.play();
	const rejectPlay = () => props.sound_fx.reject.play();
	const rejectPlayerPlay = () => props.sound_fx.reject_player.play();
	const successPlay = () => props?.sound_fx?.success.play();
	const failPlay = () => props?.sound_fx?.fail.play();
	const extraGoalPlay = () => {
		if(props.sound_fx.extra_goal == undefined) return;
		props.sound_fx.extra_goal.play();
	};
	/* SOUNDS - end */

	const preventLeaving = (e) => {
		e.returnValue = "There are unsaved changes. Leave now?";
	};
	
	const resetGame = () => {
		setFullLoaded(false);
		if(refs.current['goal_keeper_player'] != undefined) {
			if(mode == 'foosball') {
				refs.current['goal_keeper_player'].stopWander();
			} else {
				refs.current['goal_keeper_player'].stopWaiting();
			}
		}

		shot_duration.current = 0;
		animations.current = false;
		game_ended.current = false;
		detector.current = false;
		actual_goals.current = 0;
		game_score.current = 0;
		barrier_init_x.current = [];
	
		gameInit();
	};

	const generateBarrierCache = () => {
		if(refs.current['foosball_bars_barrier'] != undefined) {
			refs.current['foosball_bars_barrier'].cache(default_cache_props);
		}

		if(is_playable.current === false) return;
		if(mode == 'foosball' || move_barrier) return;

		sorted_barrier_players.map((b) => {
			if(refs.current[`barrier_${b}`] == undefined) return;
			refs.current[`barrier_${b}`].cache(default_cache_props);
		});
	};

	const generateLogosCache = () => {
		if(refs.current['logos'] != undefined) {
			refs.current['logos'].cache(default_cache_props);
		}
	};

	const generateFieldCache = () => {
		if(refs.current['bg'] != undefined) {
			refs.current['bg'].cache(default_cache_props);
		}

		if(refs.current['foosball_field_area'] != undefined) {
			refs.current['foosball_field_area'].cache(default_cache_props);
		}

		if(refs.current['field_area'] != undefined) {
			refs.current['field_area'].cache(default_cache_props);
		}

		if(refs.current['bg_img_lines'] != undefined) {
			refs.current['bg_img_lines'].cache(default_cache_props);
		}

		if(refs.current['goal_target_visual'] != undefined) {
			refs.current['goal_target_visual'].cache(default_cache_props);
		}

		if(refs.current['bg_cover'] != undefined) {
			refs.current['bg_cover'].cache(default_cache_props);
		}
	};

	const generateCache = () => {
		if(refs.current['intersection_tapper'] != undefined) {
			refs.current['intersection_tapper'].cache(default_cache_props);
		}

		if(refs.current['field_lines'] != undefined) {
			refs.current['field_lines'].cache(default_cache_props);
		}

		if(refs.current['ball_image'] != undefined) {
			refs.current['ball_image'].cache(default_cache_props);
		}

		if(refs.current['ball_icon'] != undefined) {
			refs.current['ball_icon'].cache(default_cache_props);
		}

		if(refs.current['foosball_bars_keeper'] != undefined) {
			refs.current['foosball_bars_keeper'].cache(default_cache_props);
		}

		generateLogosCache();
		generateFieldCache();
		generateBarrierCache();
	};

	const initBarrierAnimation = () => {
		if(mode != 'foosball' && move_barrier === false) return;
		if(is_playable.current === false) return;

		sorted_barrier_players.map((b) => {
			if(refs.current[`barrier_${b}_player`] != undefined) {
				if(move_barrier) {
					refs.current[`barrier_${b}_player`].wander(size.current.w / 3, level, barrier_init_x.current[b].speed, barrier_init_x.current[b].direction);
					return;
				}

				refs.current[`barrier_${b}_player`].wander(goal_target_w / 5, level);
			}
		});
	};

	const initGoalKeeperAnimation = () => {
		if(refs.current['goal_keeper_player'] == undefined) return;
		if(is_playable.current === false) return;

		initBarrierAnimation();

		if(mode == 'foosball') {
			refs.current['goal_keeper_player'].wander(goal_target_w / 2);
			return;
		}
		
		refs.current['goal_keeper_player'].startWaiting();
	};

	const calcBarrierInitProps = () => {
		if(mode == 'foosball' || move_barrier === false) return;

		barrier_init_x.current = [];
		sorted_barrier_players.map((b, index) => {
			const seed = Math.random();

			const direction = (index == 0) ? ((seed < 0.5) ? -1 : 1) : -1 * barrier_init_x.current[index - 1].direction;

			barrier_init_x.current.push({
				speed:b * 0.5 * seed,
				direction,
			});
		});
	};

	const mountGame = () => {
		if(refs.current['ball'] != undefined) {
			refs.current['ball'].setAttrs({
				opacity:1,
			});
		}

		if(is_playable.current === false) return;

		calcBarrierInitProps();
		initGoalKeeperAnimation();

		if(refs.current['whole_tapper'] != undefined) {
			refs.current['whole_tapper'].setAttrs({
				y:size.current.h + 10,
			});
		}

		props.startTimer();
	};

	const gameInit = (from_resize = false) => {
		if(props.adaptToContainer != undefined) {
			props.adaptToContainer((_size) => {
				size.current = { w:_size, h:_size * h_prop };
				
				ball_coords.current = {
					x:size.current.w / 2,
					y:1.65 * size.current.h / 2,
				};

				if(!from_resize) {
					setTimeout(() => {
						generateCache();
						mountGame();
					}, 300);
				}

				setFullLoaded(true);
			});
		}
	};

	useEffect(() => {
		generateBarrierCache();

		const barrier_length = parseInt(stage?.penalty_barrier_length ?? '1');
		if(barrier_length > 0) {
			barrier_skin_colors.current = [];
			barrier_hair_colors.current = [];

			for(let i = 0; i < barrier_length; i++) {
				barrier_skin_colors.current.push(getRandomColor('skin'));
				barrier_hair_colors.current.push(getRandomColor('hair'));
			}
		}
	}, [
		stage.penalty_barrier_length,
		stage.penalty_barrier_shirt_color,
		stage.penalty_barrier_pants_color,
		stage.penalty_barrier_boot_color,
		stage.penalty_barrier_skin_color,
		stage.penalty_barrier_hair_color,
	]);

	useEffect(() => {
		generateFieldCache();
	}, [
		stage.bg_color,
		stage.penalty_display_field_texture,
		stage.penalty_field_lines_color,
		stage.penalty_foosball_cover_color,
	]);

	useEffect(() => {
		generateLogosCache();
	}, [
		stage.penalty_left_logo,
		stage.penalty_right_logo,
	]);

	useEffect(() => {
		resetGame();
		generateCache();
	}, [
		stage.penalty_mode,
	]);

	useEffect(() => {
		window.onresize = () => {
			gameInit(true);
		}

		if(!is_builder) window.addEventListener('beforeunload', preventLeaving);

		return () => {
			if(props.endSoundtrack != undefined) props.endSoundtrack(true);

			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);
		};
	}, []);

	const unmountBoardAnimation = (is_solved = true, cb) => {
		is_playable.current = false;

		if(refs.current['ball'] == undefined) return;

		(is_solved === false) ? failPlay() : successPlay();

		setTimeout(() => {
			if(refs.current['goal_keeper_player'] != undefined) {
				refs.current['goal_keeper_player'].stand();
				if(mode == 'foosball') refs.current['goal_keeper_player'].stopWander();
			}

			if(mode == 'foosball' || move_barrier) {
				sorted_barrier_players.map((b) => {
					if(refs.current[`barrier_${b}_player`] == undefined) return;
					refs.current[`barrier_${b}_player`].stopWander();
				});
			}
		}, 500);

		refs.current['ball'].to({
			opacity:0,
			duration:1.5,
		});

		if(refs.current['whole_tapper'] != undefined) {
			refs.current['whole_tapper'].setAttrs({
				fill:(is_solved !== false) ? 'green' : 'red',
				y:0,
			});

			refs.current['whole_tapper'].to({
				opacity:0.7,
				duration:4,
				onFinish:() => {
					cb();
				},
			});

			return;
		}
	};

	useImperativeHandle(ref, () => ({
		stopGame(success_game, cb) {
			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);

			unmountBoardAnimation(success_game, () => {
				setTimeout(() => {
					if(success_game === false) success_game = 2;
					cb(success_game);
				}, (delay_to_success * 1000));
			});
		},
	}), []);

	if(!full_loaded) return null;

	const canvas_scale_w = size.current.w / default_size_w;
	const canvas_scale_h = size.current.h / default_size_h;

	const goal_keeper_w = size.current.w / 7;
	const goal_keeper_h = 49 / 75 * goal_keeper_w;
	const goal_keeper_x = size.current.w / 2;
	const goal_keeper_y = goal_target_y + goal_keeper_h / 3;
	const goal_target_collision_y = goal_target_y / 20;
	const goal_target_depth = (mode == 'foosball') ? (21 * goal_target_collision_y) : (19 * goal_target_collision_y);

	const barrier_unit_h = goal_keeper_h;
	const barrier_unit_w = 56 / 48 * barrier_unit_h;
	const barrier_unit_h_sep = (mode == 'foosball') ? size.current.w / 4 : (56 / 48 * (barrier_unit_h - 15));
	const barrier_unit_v_sep = 56 / 48 * (5);

	const barrier_y = goal_keeper_y + 0.5 * (initial_ball_coords.y - goal_keeper_y);
	const barrier_w = barrier_unit_h_sep * (barrier_length - 1) + barrier_unit_w;
	const barrier_min_x = size.current.w / 5;
	const barrier_max_x = size.current.w - barrier_min_x - barrier_w;

	const ball_radius = 30 * scenario_scale;
	const ball_target_w = 1.2 * ball_radius;
	const ball_target_h = 5;

	const updateScore = (is_goal = true) => {
		if((!is_builder || is_test) && props.doHit != undefined) props.doHit(checkSolved());

		game_score.current = Math.max(0, game_score.current + ((is_goal) ? points_for_goal : points_for_fail));
		if(is_goal) actual_goals.current = actual_goals.current + 1;
		if(props.onScore != undefined) props.onScore(game_score.current);
		if(props.updateActualGoals != undefined) props.updateActualGoals(actual_goals.current);

		isSolved();
	};

	const haveIntersection = (r1, r2) => {
		return !(
			r2.x > r1.x + r1.width ||
			r2.x + r2.width < r1.x ||
			r2.y > r1.y + r1.height ||
			r2.y + r2.height < r1.y
		);
	};

	const calcBarrierX = () => {
		if(mode == 'foosball') {
			return (size.current.w - barrier_w) / 2;
		}

		let x = barrier_min_x;
		if(relocate_ball === false && Math.random() > 0.5) {
			x = barrier_max_x;
		} else if(ball_coords.current.x < initial_ball_coords.x) {
			x = barrier_max_x;
		}

		return x;
	}

	const resetBallPosition = () => {
		if(refs.current['shooter'] != undefined) refs.current['shooter'].resetShooter(reset_props);

		if(refs.current['ball'] == undefined) return;

		refs.current['ball'].to({
			...reset_props,
			x:ball_coords.current.x,
			y:initial_ball_coords.y,
		});
	};

	const resetGoalKeeper = () => {
		if(refs.current['goal_keeper'] == undefined) return;

		refs.current['goal_keeper'].to({
			x:goal_keeper_x,
			duration:1,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				setTimeout(() => {
					initGoalKeeperAnimation();
				}, 500);
			},
		});
	};

	const finalMoves = () => {
		resetObstacles();

		if(mode == 'foosball') return;

		if(refs.current['goal_keeper'] == undefined) return;

		refs.current['goal_keeper'].to({
			x:-1.2 * goal_keeper_w,
			duration:2.7,
			easing:Konva.Easings.StrongEaseInOut,
		});

		if(barrier_length > 0) {
			sorted_barrier_players.map((b) => {
				setTimeout(() => {
					if(refs.current[`barrier_${b}`] == undefined) return;

					const final_barrier_x = (refs.current[`barrier_${b}`].x() < size.current.w / 2) ? -1.2 * goal_keeper_w : size.current.w + 1.2 * goal_keeper_w;

					refs.current[`barrier_${b}`].to({
						x:final_barrier_x,
						duration:2.5,
						easing:Konva.Easings.StrongEaseInOut,
					});
				}, (b * 100 + 300));
			});
		}
	};

	const resetObstacles = () => {
		if(refs.current['left_post_alias'] == undefined) return;

		const anim_defaults = {
			opacity:0,
			duration:1,
			easing:Konva.Easings.StrongEaseInOut,
		};

		refs.current['left_post_alias'].to({
			...anim_defaults,
		});

		refs.current['right_post_alias'].to({
			...anim_defaults,
		});
	};

	const resetBall = () => {
		if(refs.current['ball'] == undefined) return;

		if(relocate_ball) {
			const ball_margin = 0.2;
			const x_min = ball_margin * size.current.w;
			const x_max = (1 - 2 * ball_margin) * size.current.w;

			ball_coords.current = {
				...initial_ball_coords,
				x:x_min + x_max * Math.random(),
			};
		}

		refs.current['ball_cross'].setAttrs({
			opacity:0,
		});

		refs.current['ball_icon'].setAttrs({
			rotation:-600,
		});

		refs.current['ball_icon'].to({
			rotation:0,
			duration:1.5,
			easing:Konva.Easings.StrongEaseInOut,
		});

		refs.current['ball'].setAttrs({
			...reset_props,
			opacity:1,
			x:-100,
			y:initial_ball_coords.y,
			angle:0,
			collision_x:false,
			collision_y:false,
		});

		refs.current['ball'].to({
			x:ball_coords.current.x,
			duration:1.5,
			easing:Konva.Easings.StrongEaseInOut,
		});

		refs.current['shooter'].moveTo({
			x:ball_coords.current.x,
		});

		relocateBarrier();
	};

	const getKeeperReactionTime = () => {
		switch(level) {
			case 'easy':
				return {
					prediction:0.4,
					reation_time:0.25,
					speed:0.4,
				};

			case 'hard':
				return {
					prediction:0.6,
					reation_time:0.1,
					speed:0.2,
				};

			case 'normal':
			default:
				return {
					prediction:0.5,
					reation_time:0.2,
					speed:0.35,
				};
		}
	};

	const moveGoalKeeper = (expected_ball_x) => {
		if(mode == 'foosball') return;

		const { prediction, reation_time, speed } = getKeeperReactionTime();

		const seed = Math.random();
		const has_medium_powers = (prediction !== false && seed < prediction) ? true : false;

		refs.current['goal_keeper_player'].stopWaiting();

		const initial_x = refs.current['goal_keeper'].x();

		let final_x = goal_keeper_x;
		if(has_medium_powers === true) {
			final_x = expected_ball_x;
		} else {
			const direction = (expected_ball_x > size.current.w / 2) ? -1 : 1;
			final_x = goal_keeper_x + direction * size.current.w * 0.3;
		}

		final_x = Math.max(goal_keeper_x / 2, Math.min(goal_keeper_w + 3 * goal_keeper_x / 2, final_x));

		const player_x_delta = Math.abs(final_x - initial_x);

		jump_needed.current = (player_x_delta > goal_target_w * 0.5) ? true : false;

		if(jump_needed.current) {
			const jump_direction = (final_x > initial_x) ? 1 : -1;
			refs.current['goal_keeper_player'].jump(jump_direction);
		}

		setTimeout(() => {
			if(refs.current['goal_keeper'] != undefined) {
				refs.current['goal_keeper'].to({
					x:final_x,
					duration:speed,
					easing:Konva.Easings.StrongEaseInOut,
				});
			}
		}, (shot_duration.current * reation_time * 1000));
	};

	const releaseShooter = (force, angle) => {
		if(is_playable.current === false) {
			refs.current['shooter'].resetShooter(reset_props);
			return;
		}

		if(Math.abs(angle) > 45 || force < 0.2) { 
			resetBallPosition();
			return;
		}

		kickPlay();

		animations.current = true;

		const initial_x = refs.current['ball'].attrs.x;
		const initial_y = refs.current['ball'].attrs.y;
		const final_y = (mode == 'foosball') ? -100 : 0;

		shot_duration.current = 1.2 - 0.6 * force;

		refs.current['shooter'].resetShooter(reset_props);

		refs.current['ball_trace'].setAttrs({
			rotation:-angle,
			x:ball_coords.current.x,
			opacity:0,
			fillLinearGradientColorStops:[0.3, 'rgba(255, 255, 255, 0)', 0.01, 'rgba(255, 255, 255, 1)'],
		});

		startAnimations();

		if(refs.current['ball_icon'] == undefined) return;

		refs.current['ball_icon'].to({
			rotation:360 * (10 * force),
			duration:1.5 * shot_duration.current,
			easing:Konva.Easings.StrongEaseInOut,
		});

		const angle_in_rad = degToRad(-angle);
		const final_ball_x = Math.tan(angle_in_rad) * (initial_y - final_y) + initial_x;
		const final_ball_x_at_keeper = Math.tan(angle_in_rad) * (initial_y - final_y - goal_keeper_y) + initial_x;

		moveGoalKeeper(final_ball_x_at_keeper);

		refs.current['ball_trace'].to({
			opacity:0.2,
			fillLinearGradientColorStops:[0.3, 'rgba(255, 255, 255, 0)', 1, 'rgba(255, 255, 255, 1)'],
			duration:1.6 * shot_duration.current,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				refs.current['ball_trace'].to({
					opacity:0,
					fillLinearGradientColorStops:[0.9, 'rgba(255, 255, 255, 0)', 1, 'rgba(255, 255, 255, 1)'],
					duration:4,
				});
			},
		});

		refs.current['ball'].setAttrs({
			angle:-angle,
		});

		refs.current['ball'].to({
			y:final_y,
			x:final_ball_x,
			duration:shot_duration.current,
			onFinish:() => {
				if(is_playable.current === false) {
					setTimeout(() => {
						finalMoves();
					}, 1000);
					return;
				}

				setTimeout(() => {
					resetObstacles();
					resetGoalKeeper();
				}, 1000);

				setTimeout(() => {
					if(refs.current['ball'] == undefined) return;

					refs.current['ball'].to({
						opacity:0,
						duration:1,
						easing:Konva.Easings.StrongEaseInOut,
						onFinish:() => {
							resetBall();
						},
					});
				}, 2500);
			},
		});
	};

	const relocateBarrier = () => {
		if(move_barrier) return;

		const anim_props = {
			duration:1,
			easing:Konva.Easings.StrongEaseInOut,
		};

		const new_x = calcBarrierX();
		
		sorted_barrier_players.map((b) => {
			if(refs.current[`barrier_${b}`] == undefined) return;

			refs.current[`barrier_${b}`].to({
				x:new_x + b * barrier_unit_h_sep,
				...anim_props,
			});
		});
	};

	const trapBall = () => {
		if(mode == 'foosball') return;
		if(refs.current['ball'] == undefined) return;

		const x_min = (size.current.w - goal_target_w) / 2 + ball_radius;
		const x_max = (size.current.w - goal_target_w) / 2 + goal_target_w - ball_radius;
		refs.current['ball'].setAttrs({
			collision_x:Math.min(x_max, Math.max(x_min, refs.current['ball'].x())),
			collision_y:refs.current['ball'].y(),
		});

		refs.current['ball_icon'].to({
			rotation:refs.current['ball_icon'].attrs.rotation + 1.5 * 360,
			duration:2,
			easing:Konva.Easings.StrongEaseOut,
		});

		refs.current['ball'].to({
			x:refs.current['ball'].attrs.collision_x,
			y:refs.current['ball'].attrs.collision_y + ball_radius,
			duration:1.5,
			easing:Konva.Easings.StrongEaseOut,
		});
	};

	const rejectBall = () => {
		if(refs.current['ball'] == undefined) return;

		refs.current['ball'].setAttrs({
			collision_x:refs.current['ball'].x(),
			collision_y:refs.current['ball'].y(),
		});

		refs.current['ball_cross'].to({
			opacity:1,
			duration:1.5,
			easing:Konva.Easings.StrongEaseOut,
		});

		refs.current['ball'].to({
			x:refs.current['ball'].attrs.collision_x + 300 * Math.random() - 150,
			y:Math.max(refs.current['limit'].attrs.y, refs.current['ball'].attrs.collision_y + size.current.h / 5),
			duration:1.5,
			easing:Konva.Easings.StrongEaseOut,
		});
	};

	const moveBall = (shooter_props) => {
		if(refs.current['ball'] == undefined) return;

		refs.current['ball'].setAttrs({
			x:ball_coords.current.x + shooter_props.x,
			y:initial_ball_coords.y + shooter_props.y,
		});
	};

	const fadeInGradientCover = () => {
		if(refs.current['intersection_tapper'] == undefined) return;

		refs.current['intersection_tapper'].setAttrs({
			opacity:0,
			y:0,
		});

		refs.current['intersection_tapper'].to({
			opacity:0.5,
			duration:1.5,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				setTimeout(() => {
					if(refs.current['intersection_tapper'] == undefined) return;

					refs.current['intersection_tapper'].to({
						opacity:0,
						duration:2,
						easing:Konva.Easings.StrongEaseInOut,
						onFinish:() => {
							if(refs.current['intersection_tapper'] == undefined) return;

							refs.current['intersection_tapper'].setAttrs({
								y:size.current.h + 10,
							});
						},
					});
				}, 1000);
			},
		});
	};

	const getReactionMessage = (type) => {
		const filtered_msgs = [ ...reaction_messages ].filter((m) => (m.type == type));
		if(filtered_msgs.length == 0) return "";

		for(let i = filtered_msgs.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[filtered_msgs[i], filtered_msgs[j]] = [filtered_msgs[j], filtered_msgs[i]];
		}

		return filtered_msgs[0]?.title ?? "";
	};

	const detectCollisions = () => {
		if(refs.current['obstacles'] == undefined || animations.current === false) return;

		if(refs.current['obstacles'].getChildren().length == 0) {
			if(detector.current !== false) requestAnimationFrame(detectCollisions);
			return;
		}

		refs.current['obstacles'].getChildren().map((item) => {
			const type = item.attrs.type;
			if(type == undefined) return;

			if(animations.current === false) return;
			if(refs.current['ball'].y() > refs.current['limit'].y()) return;

			if(haveIntersection(item.getClientRect(), refs.current['ball_target'].getClientRect())) {
				if(item.attrs.alias != undefined) {
					refs.current[item.attrs.alias].to({
						opacity:1,
						duration:0.5,
						easing:Konva.Easings.BounceEaseOut,
					});
				}

				animations.current = false;

				setTimeout(() => {
					refs.current['goal_keeper_player'].stand();

					if(mode == 'foosball') {
						refs.current['goal_keeper_player'].stopWander();
					}

					calcBarrierInitProps();

					if(mode == 'foosball' || move_barrier) {
						sorted_barrier_players.map((b) => {
							refs.current[`barrier_${b}_player`].stopWander();
						});
					}
				}, 500);

				if(['goal', 'goal_l', 'goal_r'].includes(type)) {
					refs.current['goal_text_c'].display(getReactionMessage((jump_needed.current) ? 'super_goal' : 'goal'), true);
					trapBall();
					fadeInGradientCover();
					updateScore(true);
					goalPlay();
					if(mode != 'foosball') {
						setTimeout(() => {
							extraGoalPlay();
						}, 200);
					}
					return;
				}

				refs.current['ball_trace'].to({
					opacity:0,
					duration:1.6 * shot_duration.current,
				});

				updateScore(false);

				if(['keeper'].includes(type)) {
					refs.current['goal_text_c'].display(getReactionMessage((jump_needed.current) ? 'super_blocked' : 'blocked'), false);
					rejectBall();
					fadeInGradientCover();
					rejectPlayerPlay();
					if(mode != 'foosball') kickPlay();
					return;
				}

				if(['barrier'].includes(type)) {
					refs.current['goal_text_c'].display(getReactionMessage('barrier'), false);
					rejectBall();
					fadeInGradientCover();
					rejectPlayerPlay()
					if(mode != 'foosball') kickPlay();
					return;
				}

				if(['out_l', 'out_r'].includes(type)) {
					refs.current['goal_text_c'].display(getReactionMessage('out'), false);
					if(mode == 'foosball') rejectBall();
					fadeInGradientCover();
					(mode == 'foosball') ? rejectPlay() : rejectPlayerPlay();
					return;
				}

				if(['left_post', 'right_post'].includes(type)) {
					refs.current['goal_text_c'].display(getReactionMessage('post'), false);
					rejectBall();
					fadeInGradientCover();
					rejectPlay();
					if(mode != 'foosball') rejectPlayerPlay();
					return;
				}
			}
		});

		if(detector.current !== false) requestAnimationFrame(detectCollisions);
	};

	const startAnimations = () => {
		game_ended.current = false;
		animations.current = true;

		detector.current = window.requestAnimationFrame(detectCollisions);
	};

	const checkSolved = () => {
		return (actual_goals.current < num_goals) ? false : true;
	};

	const isSolved = () => {
		let is_solved = checkSolved();

		if(is_solved) {
			successPlay();

			if(props.onEnded != undefined) props.onEnded(true);
		}

		return is_solved;
	};

	const main_classes = ['canvas_cont'];
	if(display_outter_border === false) main_classes.push('not_bordered');

	const ball_scale = 0.65;

	const barrier_x = calcBarrierX();

	const x_displ = (size.current.w - 1200 * scenario_scale) / 2;

	const extra_goal_w = Math.max(0, goal_target_w - goal_target_w_original * scenario_scale);

	return (
		<Stage
			ref={(node) => { refs.current['stage'] = node; }}
			class={main_classes.join(" ")}
			width={size.current.w}
			height={size.current.h}
		>
			<Layer ref={(node) => { refs.current['layer'] = node; }}>
				<Rect
					ref={(node) => { refs.current['bg'] = node; }}
					width={size.current.w}
					height={size.current.h}
					fill={bg_color}
				/>

				{(mode != 'foosball' && display_field_texture) && (
					<Group
						ref={(node) => { refs.current['bg_img_lines'] = node; }}
						opacity={0.05}
						scaleX={size.current.w / 800}
					>
						<Rect
							x={0}
							width={60}
							height={size.current.h}
							fill='black'
						/>

						<Rect
							x={285}
							width={230}
							height={size.current.h}
							fill='black'
						/>

						<Rect
							x={740}
							width={60}
							height={size.current.h}
							fill='black'
						/>
					</Group>
				)}

				{(mode == 'foosball') && (
					<Group
						ref={(node) => { refs.current['foosball_field_area'] = node; }}
						opacity={0.7}
						scaleX={scenario_scale}
						scaleY={scenario_scale}
						x={x_displ}
					>
						<FoosballFieldLines
							color={field_lines_color}
							final_w={goal_target_w}
							original_w={goal_target_w_original * scenario_scale}
						/>
					</Group>
				)}

				{(mode != 'foosball') && (
					<Group
						ref={(node) => { refs.current['field_area'] = node; }}
						opacity={0.5}
						scaleX={scenario_scale}
						scaleY={scenario_scale}
						x={x_displ}
					>
						<FieldLines
							color={field_lines_color}
						/>
					</Group>
				)}

				{(mode == 'foosball') && (
					<>
						<Group
							ref={(node) => { refs.current['foosball_bars_keeper'] = node; }}
							y={goal_keeper_y + goal_keeper_h / 2}
							scaleY={1 - 0.6 * scenario_scale}
							offsetY={10 / 2}
						>
							<FoosballBar
								w={size.current.w}
							/>
						</Group>

						{(barrier_length > 0) && (
							<Group
								ref={(node) => { refs.current['foosball_bars_barrier'] = node; }}
								y={barrier_y + barrier_unit_h / 2}
								scaleY={1 - 0.6 * scenario_scale}
								offsetY={10 / 2}
							>
								<FoosballBar
									w={size.current.w}
								/>
							</Group>
						)}
					</>
				)}

				<Rect
					ref={(node) => { refs.current['ball_trace'] = node; }}
					x={ball_coords.current.x}
					y={initial_ball_coords.y}
					offsetX={ball_scale * 60 * canvas_scale_w / 2}
					offsetY={size.current.h}
					opacity={0}
					width={ball_scale * 60 * canvas_scale_w}
					height={size.current.h}
					fillLinearGradientStartPoint={{ x:0, y:size.current.h }}
					fillLinearGradientEndPoint={{ x:0, y:0 }}
					fillLinearGradientColorStops={[0.3, 'rgba(255, 255, 255, 0)', 1, 'rgba(255, 255, 255, 1)']}
				/>

				<Group
					ref={(node) => { refs.current['obstacles'] = node; }}
				>
					<Rect
						width={goal_target_w}
						height={goal_target_h * 5}
						offsetX={goal_target_w / 2}
						x={size.current.w / 2}
						y={goal_target_collision_y - goal_target_h * 5}
						fill='yellow'
						opacity={(is_debug) ? 0.5 : 0}
						type='goal'
					/>

					<Rect
						width={goal_target_h * 5}
						height={goal_target_depth - 25}
						offsetX={goal_target_w / 2}
						x={size.current.w / 2 - goal_target_h * 5}
						fill='yellow'
						opacity={(is_debug) ? 0.5 : 0}
						type='goal_l'
					/>

					<Rect
						width={goal_target_h * 5}
						height={goal_target_depth - 25}
						offsetX={goal_target_w / 2}
						x={size.current.w / 2 + goal_target_w}
						fill='yellow'
						opacity={(is_debug) ? 0.5 : 0}
						type='goal_r'
					/>

					<Rect
						width={size.current.w}
						height={20}
						x={-(size.current.w + goal_target_w) / 2}
						y={goal_target_y - 20}
						fill='red'
						opacity={(is_debug) ? 0.5 : 0}
						type='out_l'
					/>

					<Rect
						width={size.current.w}
						height={20}
						x={(size.current.w + goal_target_w) / 2}
						y={goal_target_y - 20}
						fill='red'
						opacity={(is_debug) ? 0.5 : 0}
						type='out_r'
					/>

					<Group
						ref={(node) => { refs.current['goal_keeper'] = node; }}
						offsetX={goal_keeper_w / 2}
						x={goal_keeper_x}
						y={goal_keeper_y}
						type='keeper'
					>
						<FootballGoalKeaper
							ref={(node) => { refs.current['goal_keeper_player'] = node; }}
							width={goal_keeper_w}
							height={goal_keeper_h}
							genre={genre}
							skin_color={(gk_skin_color != 'random') ? gk_skin_color : goal_keeper_skin_color.current}
							hair_color={(gk_hair_color != 'random') ? gk_hair_color : goal_keeper_hair_color.current}
							shirt_color={stage.penalty_goal_keeper_shirt_color}
							pants_color={stage.penalty_goal_keeper_pants_color}
							glove_color={stage.penalty_goal_keeper_glove_color}
							boot_color={stage.penalty_goal_keeper_boot_color}
						/>
					</Group>

					<Rect
						width={20}
						height={20}
						x={(size.current.w - goal_target_w) / 2 - 10}
						y={goal_target_y - 10}
						fill='blue'
						type='left_post'
						alias='left_post_alias'
						opacity={(is_debug) ? 0.5 : 0}
					/>

					<Rect
						width={20}
						height={20}
						x={goal_target_w + (size.current.w - goal_target_w) / 2 - 10}
						y={goal_target_y - 10}
						fill='blue'
						type='right_post'
						alias='right_post_alias'
						opacity={(is_debug) ? 0.5 : 0}
					/>

					{barrier_length > 0 && sorted_barrier_players.map((b) => {
						let x_displ = barrier_x + b * barrier_unit_h_sep;
						let y_displ = barrier_y;
						if(mode != 'foosball') {
							if(move_barrier) {
								y_displ = barrier_y - (barrier_length - (b + 1)) * 1.5 * barrier_unit_h + barrier_unit_h / 2;

								x_displ = (size.current.w - barrier_unit_w) / 2;
							} else if(b % 2 != 0) {
								y_displ = barrier_y - barrier_unit_v_sep;
							}
						}

						return (
							<Group
								key={`barrier_${b}`}
								ref={(node) => { refs.current[`barrier_${b}`] = node; }}
								x={x_displ}
								y={y_displ}
								type='barrier'
							>
								<FootballBarrier
									ref={(node) => { refs.current[`barrier_${b}_player`] = node; }}
									index={b}
									width={barrier_unit_w}
									height={barrier_unit_h}
									genre={genre}
									skin_color={(b_skin_color != 'random') ? b_skin_color : barrier_skin_colors.current[b]}
									hair_color={(b_hair_color != 'random') ? b_hair_color : barrier_hair_colors.current[b]}
									shirt_color={stage.penalty_barrier_shirt_color}
									shirt_image={props?.indexed_images?.penalty_shirt_barrier_image}
									pants_color={stage.penalty_barrier_pants_color}
									glove_color={stage.penalty_barrier_glove_color}
									boot_color={stage.penalty_barrier_boot_color}
								/>
							</Group>
						)
					})}

					<Line
						ref={(node) => { refs.current['limit'] = node; }}
						y={(barrier_length > 0) ? (barrier_y + 3 * barrier_unit_h) : (goal_keeper_y + 3 * goal_keeper_h)}
						points={[0, 0, size.current.w, 0]}
						strokeWidth={1}
						stroke='black'
						opacity={(is_debug) ? 0.5 : 0}
					/>

					<Group
						ref={(node) => { refs.current['ball'] = node; }}
						x={ball_coords.current.x}
						y={initial_ball_coords.y}
						angle={0}
						collision_x={false}
						collision_y={false}
					>
						<Ellipse
							radius={{
								x:30 * ball_scale * canvas_scale_h,
								y:30 * ball_scale * canvas_scale_h * 0.5,
							}}
							fill='black'
							opacity={0.2}
							y={30 * ball_scale * canvas_scale_h * 0.8}
						/>

						<Group
							ref={(node) => { refs.current['ball_icon'] = node; }}
							rotation={0}
						>
							{(ball_image !== false) ? (
								<Circle
									ref={(node) => { refs.current['ball_image'] = node; }}
									radius={ball_o_size}
									fillPatternImage={ball_image.fillPatternImage}
									fillPatternRepeat="no-repeat"
									fillPatternScaleX={ball_scale * ball_size}
									fillPatternScaleY={ball_scale * ball_size}
									fillPatternOffsetX={ball_o_size}
									fillPatternOffsetY={ball_o_size}
								/>
							) : (
								<FootballBall
									ball_props={{
										scaleX:ball_scale * canvas_scale_h,
										scaleY:ball_scale * canvas_scale_h,
									}}
								/>
							)}
						</Group>

						<Line
							ref={(node) => { refs.current['ball_cross'] = node; }}
							points={[30, -21.43, 21.43, -30, 0, -8.57, -21.43, -30, -30, -21.43, -8.57, 0, -30, 21.43, -21.43, 30, 0, 8.57, 21.43, 30, 30, 21.43, 8.57, 0]}
							closed={true}
							fill='red'
							scaleX={0.75 * ball_scale}
							scaleY={0.75 * ball_scale}
							opacity={0}
						/>

						<Rect
							ref={(node) => { refs.current['ball_target'] = node; }}
							width={ball_scale * ball_target_w * canvas_scale_h}
							height={ball_target_h * canvas_scale_h}
							fill='purple'
							opacity={(is_debug) ? 0.5 : 0}
							offsetX={ball_scale * ball_target_w * canvas_scale_h / 2}
							y={-(ball_radius) * ball_scale * canvas_scale_h}
						/>
					</Group>
				</Group>

				{(mode == 'foosball') && (
					<Group
						ref={(node) => { refs.current['bg_cover'] = node; }}
						opacity={(is_debug) ? 0.2 : 1}
					>
						<Rect
							width={1200}
							height={165 * scenario_scale}
							fillLinearGradientStartPoint={{ x:0, y:165 * scenario_scale }}
							fillLinearGradientEndPoint={{ x:0, y:0 }}
							fillLinearGradientColorStops={[0, 'rgba(0, 0, 0, 0)', 1, 'rgba(0, 0, 0, 0.7)']}
						/>

						<Path
							data={`M520,165H${212.5 + extra_goal_w}l-8.05-99.82C${201.94 + extra_goal_w},34.01,${175.91 + extra_goal_w},10,${144.6 + extra_goal_w},10h-${289.29 + 2 * extra_goal_w}c-31.27,0-57.29,24.01-59.81,55.18L-${212.5 + extra_goal_w},165H-520V-20h1040V165z`}
							scaleX={scenario_scale}
							scaleY={scenario_scale}
							fill={cover_color}
							x={size.current.w / 2}
							strokeWidth={4}
							stroke={field_lines_color}
						/>
					</Group>
				)}

				{(left_logo !== false || right_logo !== false) && (
					<Group
						ref={(node) => { refs.current['logos'] = node; }}
						scaleX={scenario_scale_w}
						scaleY={scenario_scale_w}
					>
						{(left_logo !== false) && (
							<Rect
								width={logo_size}
								height={logo_size}
								x={20 * scenario_scale_w / scenario_scale}
								y={7.5 * scenario_scale / scenario_scale_w}
								fillPatternImage={left_logo.fillPatternImage}
								fillPatternRepeat="no-repeat"
								fillPatternScaleX={left_logo_size}
								fillPatternScaleY={left_logo_size}
							/>
						)}

						{(right_logo !== false) && (
							<Rect
								width={logo_size}
								height={logo_size}
								x={800 - logo_size - 20 * scenario_scale_w / scenario_scale}
								y={7.5 * scenario_scale / scenario_scale_w}
								fillPatternImage={right_logo.fillPatternImage}
								fillPatternRepeat="no-repeat"
								fillPatternScaleX={right_logo_size}
								fillPatternScaleY={right_logo_size}
							/>
						)}
					</Group>
				)}

				{(mode != 'foosball') && (
					<Group
						ref={(node) => { refs.current['goal_target_visual'] = node; }}
						scaleX={scenario_scale}
						scaleY={scenario_scale}
						x={size.current.w / 2}
					>
						<GoalTargetVisual
							color={field_lines_color}
							final_w={goal_target_w}
							original_w={goal_target_w_original * scenario_scale}
						/>
					</Group>
				)}

				<Line
					ref={(node) => { refs.current['left_post_alias'] = node; }}
					points={[ 0, 0, 0, goal_target_h ]}
					x={size.current.w / 2 - goal_target_w / 2}
					y={goal_target_y - goal_target_h}
					opacity={0}
					strokeWidth={7 * scenario_scale}
					stroke='red'
					lineCap='round'
					lineJoin='round'
				/>

				<Line
					ref={(node) => { refs.current['right_post_alias'] = node; }}
					points={[ 0, 0, 0, goal_target_h ]}
					x={(goal_target_w + size.current.w) / 2}
					y={goal_target_y - goal_target_h}
					opacity={0}
					strokeWidth={7 * scenario_scale}
					stroke='red'
					lineCap='round'
					lineJoin='round'
				/>

				<GameShooter
					ref={(node) => { refs.current['shooter'] = node; }}
					shooter_props={{
						...ball_coords.current,
						scaleX:ball_scale * canvas_scale_h,
						scaleY:ball_scale * canvas_scale_h,
					}}
					color={shooter_color}
					game_display_path={stage?.game_display_path ?? 'full'}
					main_stage_axis={size.current.h}
					apply_heisenberg={stage?.game_apply_heisenberg ?? 'normal'}
					onStart={moveBall}
					onRelease={releaseShooter}
					is_debug={is_debug}
				/>

				<Rect
					ref={(node) => { refs.current['intersection_tapper'] = node; }}
					y={size.current.h + 10}
					width={size.current.w}
					height={size.current.h}
					opacity={0}
					fillRadialGradientStartPoint={{ x:size.current.w / 2, y:150 * scenario_scale }}
					fillRadialGradientStartRadius={0}
					fillRadialGradientEndPoint={{ x:size.current.w / 2, y:150 * scenario_scale }}
					fillRadialGradientEndRadius={800 * scenario_scale}
					fillRadialGradientColorStops={[ 0, 'rgba(0, 0, 0, 0)', 1, 'rgba(0, 0, 0, 1)' ]}
				/>

				{(!is_builder || is_test) && (
					<Rect
						ref={(node) => { refs.current['whole_tapper'] = node; }}
						y={0}
						width={size.current.w}
						height={size.current.h}
						fill='white'
						opacity={0}
					/>
				)}

				<Group
					ref={(node) => { refs.current['goal_text'] = node; }}
					x={x_displ}
				>
					<FeedbackMessage
						ref={(node) => { refs.current['goal_text_c'] = node; }}
						msg_props={{
							scaleX:scenario_scale,
							scaleY:scenario_scale,
						}}
						visible_y={840 * scenario_scale}
						total_goals={num_goals}
					/>
				</Group>
			</Layer>
		</Stage>
	);
});

export default PenaltyCanvas;